import type { IGameHost } from './IGameHost';
import type { IScenes } from '../rendering/IScenes';
import type { SceneInitData } from './IScene';
import { SceneRegistry } from './SceneRegistry';
import { FramePhaseBudget } from '../diagnostics/FramePhaseBudget';
import { gameLogger } from '../../utils/GameLogger';

/**
 * Config for {@link GameHost} construction. Only `width`/`height`/`parent` are
 * used — they size the hidden placeholder canvas. `backgroundColor` is accepted
 * for call-site compatibility but the real rendering surface is PixiOverlayHost.
 */
export interface GameHostConfig {
  width: number;
  height: number;
  parent?: HTMLElement;
  backgroundColor?: number | string;
}

/** Maximum delta passed to scene ticks. Prevents physics/animation spirals
 *  when the browser pauses rAF (occlusion, display sleep) and then resumes
 *  with a multi-second gap. */
const MAX_DELTA_MS = 100;

/**
 * GameHost — the IGameHost impl.
 *
 * Owns:
 *   - a hidden placeholder canvas (display:none), kept only for the few
 *     consumers that still query `host.canvas`; PixiOverlayHost is the sole
 *     gameplay-rendering surface
 *   - the substrate-agnostic SceneRegistry (gameplay scene lifecycle)
 *   - the rAF loop that forwards each frame to the active scene
 *
 * H10 Arc λ removed the Phaser engine entirely (uninstalled at λ.11). The
 * `PhaserGameStub` placeholder it left behind was deleted once nothing depended
 * on its Phaser.Game-shaped surface — the host now creates and owns its canvas
 * directly.
 */
export class GameHost implements IGameHost {
  private readonly _canvas: HTMLCanvasElement;
  private readonly _scenes: IScenes;
  private readonly _sceneRegistry: SceneRegistry;
  private destroyed = false;
  private _lastTickMs = 0;

  // Phase H10 Arc λ.9 Stage B4 — rAF tick driver. Started after first
  // scene start; cancelled in destroy().
  private rafId: number | null = null;
  private lastTickTime: number | null = null;

  /** Tick-error throttle state — see {@link GameHost.reportTickError}. */
  private static readonly TICK_ERROR_BURST = 3;
  private static readonly TICK_ERROR_INTERVAL_MS = 1000;
  private tickErrorCount = 0;
  private tickErrorSuppressed = 0;
  private tickErrorLastReportMs = 0;

  constructor(config: GameHostConfig) {
    // Hidden placeholder canvas. PixiOverlayHost owns the real rendering
    // surface; this exists only for backwards-compat with consumers that
    // query `host.canvas`.
    this._canvas = typeof document !== 'undefined'
      ? document.createElement('canvas')
      : ({} as HTMLCanvasElement);
    this._canvas.width = config.width;
    this._canvas.height = config.height;
    this._canvas.style.display = 'none';
    if (config.parent && typeof config.parent.appendChild === 'function') {
      config.parent.appendChild(this._canvas);
    }

    this._scenes = new SceneRegistryScenesShim(this);
    this._sceneRegistry = new SceneRegistry();
  }

  get canvas(): HTMLCanvasElement {
    return this._canvas;
  }

  get scenes(): IScenes {
    return this._scenes;
  }

  /** Phase H10 Arc λ.9 Stage B4 — substrate-agnostic scene state machine.
   *  Gameplay code registers scenes here. */
  get sceneRegistry(): SceneRegistry {
    return this._sceneRegistry;
  }

  /** Time the last `SceneRegistry.tick()` took (ms). Exposed for freeze
   *  attribution so we can tell if the rAF stall lives in scene update. */
  get lastTickMs(): number {
    return this._lastTickMs;
  }

  /** Phase H10 Arc λ.9 Stage B4 — start the rAF tick loop. Idempotent —
   *  safe to call multiple times. Each frame forwards to SceneRegistry.tick
   *  which invokes the active scene's update(time, delta). */
  startTickLoop(): void {
    if (this.rafId !== null) return;
    const tick = (time: number) => {
      if (this.destroyed) return;
      const rawDelta = this.lastTickTime === null ? 16.67 : time - this.lastTickTime;
      const delta = Math.min(rawDelta, MAX_DELTA_MS);
      this.lastTickTime = time;
      try {
        const t0 = performance.now();
        this._sceneRegistry.tick(time, delta);
        this._lastTickMs = performance.now() - t0;
        FramePhaseBudget.record('scene', this._lastTickMs);
      } catch (err) {
        // Per-frame errors shouldn't kill the loop — but they must not be
        // invisible either. This catch is the ONLY handler for the whole client
        // game loop (`SceneRegistry.tick` has none of its own), and the log line
        // had been commented out, so a system throwing sixty times a second
        // produced zero console output, zero gameLogger, zero DomainLog —
        // indistinguishable from a healthy frame. That silently defeated the
        // freeze-attribution work, whose premise is that a stalled client leaves
        // evidence somewhere.
        this.reportTickError(err);
      }
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  /**
   * Report a tick error, throttled at the call site.
   *
   * The throttle lives here rather than in the logger because `gameLogger`
   * exempts ERROR from rate-limiting AND from duplicate suppression: both
   * branches gate on `entry.level < LogLevel.WARN` (GameLogger.ts:152 and :167)
   * and `ERROR` (5) is above `WARN` (4), so neither ever fires for an error.
   * The `ALWAYS_SHOW` category list is an additional bypass, not the only one —
   * so 'scene' being absent from it buys nothing. An unthrottled `error()` in a
   * 60 Hz loop therefore pushes ~60 entries per second into the log buffer,
   * every registered listener and every transport, during precisely the stall
   * it exists to diagnose.
   *
   * First `TICK_ERROR_BURST` throws report immediately, so a real failure is
   * visible at once. After that, at most one report per second, carrying the
   * number suppressed since the last one so the rate is never hidden.
   */
  private reportTickError(err: unknown): void {
    this.tickErrorCount += 1;
    const now = performance.now();

    if (this.tickErrorCount <= GameHost.TICK_ERROR_BURST) {
      this.tickErrorLastReportMs = now;
      gameLogger.error('scene', '[GameHost] update tick error', err);
      return;
    }

    this.tickErrorSuppressed += 1;
    if (now - this.tickErrorLastReportMs < GameHost.TICK_ERROR_INTERVAL_MS) return;

    const suppressed = this.tickErrorSuppressed;
    this.tickErrorSuppressed = 0;
    this.tickErrorLastReportMs = now;
    gameLogger.error(
      'scene',
      `[GameHost] update tick error (+${suppressed} suppressed, ${this.tickErrorCount} total)`,
      err,
    );
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this._sceneRegistry.destroy();
    // Remove the placeholder canvas from the DOM.
    if (this._canvas.parentNode) {
      this._canvas.parentNode.removeChild(this._canvas);
    }
  }
}

/**
 * IScenes adapter that routes through the host's SceneRegistry.
 *
 * Phase H10 Arc λ.9 Stage B4 — start/stop/restart drive the substrate-agnostic
 * registry; isActive reads the registry's active-key state.
 */
class SceneRegistryScenesShim implements IScenes {
  constructor(private readonly host: GameHost) {}

  start(sceneKey: string, data?: unknown): void {
    void this.host.sceneRegistry.start(sceneKey, (data ?? {}) as SceneInitData);
    // Ensure the rAF tick loop is running once a scene has been started.
    this.host.startTickLoop();
  }

  stop(sceneKey: string): void {
    this.host.sceneRegistry.stop(sceneKey);
  }

  restart(data?: unknown): void {
    void this.host.sceneRegistry.restart((data ?? {}) as SceneInitData);
  }

  isActive(sceneKey: string): boolean {
    return this.host.sceneRegistry.isActive(sceneKey);
  }
}

/** Re-export IScene for convenience — callers building scene classes
 *  import the contract from here. */
export type { IScene } from './IScene';
